import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

// Use relative path to go through Vite proxy (configured in vite.config.ts)
// In production, this would be configured via environment variable
const API_BASE_URL = '/api/v1';

// Create axios instance with base configuration.
// withCredentials ensures HttpOnly auth cookies (at_{userId}, rt_{userId}) are
// sent on every request, including cross-origin requests through the Vite proxy.
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ---------------------------------------------------------------------------
// Active user tracking (non-sensitive: just a UUID for multi-session support)
// The filter on the backend reads X-User-Id to know which at_{userId} cookie
// to validate. No token ever lives in JS memory or localStorage.
// ---------------------------------------------------------------------------
let activeUserId: string | null = null;
let activeOrganizationId: string | null = null;

export function setActiveUserId(id: string | null): void {
  activeUserId = id;
}

export function getActiveUserId(): string | null {
  return activeUserId;
}

export function setActiveOrganizationId(id: string | null): void {
  activeOrganizationId = id;
}

export function getActiveOrganizationId(): string | null {
  return activeOrganizationId;
}

// Clear session metadata from localStorage and redirect to login
let isRedirecting = false;
function clearAuthAndRedirect(): void {
  if (isRedirecting) return; // Prevent multiple redirects
  isRedirecting = true;
  localStorage.removeItem('sessions');
  localStorage.removeItem('activeSessionIndex');
  window.location.href = '/login';
}

// Public auth endpoints that should not trigger cookie-refresh retry
// '/sat/open/' — the anonymous Open Dashboard surface (specs/009). Nearly
// every call under this prefix carries no session at all, so a 401 there is
// never "the access token expired" — attempting a refresh (which requires
// activeUserId) and, on failure, calling clearAuthAndRedirect() (wipes
// localStorage.sessions and hard-redirects to /login), would be actively
// wrong for a signed-out visitor. This also covers the one authenticated
// route on the prefix, /sat/open/import (signup-time progress import): a
// stale access token there should surface as a failed import, not force a
// full session teardown mid-signup.
const PUBLIC_PATHS = ['/auth/register', '/auth/login', '/auth/refresh', '/invites/', '/sat/open/'];

// Track if we're currently refreshing to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshPromise: Promise<void> | null = null;

// Ask the server to rotate the refresh token and issue a new access token.
// The rt_{userId} HttpOnly cookie is sent automatically by the browser.
// The new at_{userId} cookie is set by the server in the response.
async function refreshAccessToken(): Promise<void> {
  if (!activeUserId) throw new Error('No active user session');

  const params: Record<string, string> = { userId: activeUserId };
  if (activeOrganizationId) {
    params.organizationId = activeOrganizationId;
  }

  await axios.post(
    `${API_BASE_URL}/auth/refresh`,
    null,
    { params, withCredentials: true }
  );
}

// Request interceptor: attach X-User-Id header so the backend filter knows
// which at_{userId} cookie to look up for the current session.
apiClient.interceptors.request.use(
  (config) => {
    if (activeUserId) {
      config.headers['X-User-Id'] = activeUserId;
    }
    const orgId = getActiveOrganizationId();
    if (orgId) {
      config.headers['X-Org-Id'] = orgId;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: on 401, attempt a cookie-based token refresh once,
// then retry the original request. If refresh also fails, force re-login.
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't attempt refresh for auth endpoints
      const isAuthEndpoint = PUBLIC_PATHS.some(path => originalRequest.url?.startsWith(path));
      if (isAuthEndpoint) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      try {
        if (!isRefreshing) {
          isRefreshing = true;
          refreshPromise = refreshAccessToken();
        }

        await refreshPromise;
        isRefreshing = false;
        refreshPromise = null;

        // Retry with the same config â€” the new at_{userId} cookie is now set
        // by the browser from the refresh response, so no header changes needed.
        return apiClient(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        refreshPromise = null;
        console.error('[api/client] Token refresh failed:', refreshError);
        clearAuthAndRedirect();
        return Promise.reject(refreshError);
      }
    }

    console.error(error);
    return Promise.reject(error);
  }
);

export default apiClient;
export { API_BASE_URL };
